import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';

const AuthContext = React.createContext();

const TOKEN_KEY = 'justb_session';

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionStorage.setItem(TOKEN_KEY, token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.removeItem(TOKEN_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`);
      return resp.data;
    } catch {
      return null;
    }
    // API and axios are stable module-level constants
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      // Restore token from sessionStorage on page refresh
      const savedToken = sessionStorage.getItem(TOKEN_KEY);
      if (savedToken) {
        setAuthHeader(savedToken);
        const me = await fetchMe();
        if (me) {
          setUser(me);
        } else {
          // Token expired or invalid — clear it
          setAuthHeader(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [fetchMe]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const { access_token } = response.data;

      setAuthHeader(access_token);

      const me = await fetchMe();
      if (me) {
        setUser(me);
        return true;
      }
      setAuthHeader(null);
      return false;
    } catch {
      setAuthHeader(null);
      return false;
    }
  };

  const register = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { username, password });
      const { access_token } = response.data;

      setAuthHeader(access_token);

      const me = await fetchMe();
      if (me) {
        setUser(me);
        return true;
      }
      setAuthHeader(null);
      return false;
    } catch {
      setAuthHeader(null);
      return false;
    }
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    setAuthHeader(null);
    setUser(null);
  }, []);

  const value = { user, login, register, logout, loading };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
